/**
 * CustomerLoginForm
 *
 * Email + password login form with a clean, minimal layout.
 * Handles form validation, loading state, and error display.
 * Uses the same useAuth().login flow as the agent login.
 *
 * Requirements: 3.3, 6.2
 */

import React, { useState } from 'react';
import styles from './CustomerLogin.module.css';

export interface CustomerLoginFormProps {
  /** Called with (email, password). Should throw on failure. */
  onSubmit: (email: string, password: string) => Promise<void>;
  isLoading: boolean;
  error: string | null;
  onClearError: () => void;
  onForgotPassword: () => void;
  onCreateAccount: () => void;
}

/** Basic email format check */
const isValidEmail = (value: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

export const CustomerLoginForm: React.FC<CustomerLoginFormProps> = ({
  onSubmit,
  isLoading,
  error,
  onClearError,
  onForgotPassword,
  onCreateAccount,
}) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailTouched, setEmailTouched] = useState(false);
  const [passwordTouched, setPasswordTouched] = useState(false);

  const emailError = emailTouched && !isValidEmail(email) ? 'Please enter a valid email address' : '';
  const passwordError = passwordTouched && !password ? 'Password is required' : '';
  const canSubmit = isValidEmail(email) && password.length > 0 && !isLoading;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailTouched(true);
    setPasswordTouched(true);
    onClearError();

    if (!canSubmit) return;
    await onSubmit(email, password);
  };

  return (
    <>
      {error && (
        <div className={`${styles.alert} ${styles.alertError}`} role="alert">
          {error}
        </div>
      )}

      <form className={styles.form} onSubmit={handleSubmit} noValidate>
        {/* Email field */}
        <div className={styles.fieldGroup}>
          <label htmlFor="customer-email" className={styles.label}>
            Email
          </label>
          <input
            id="customer-email"
            type="email"
            className={`${styles.input} ${emailError ? styles.inputError : ''}`}
            value={email}
            onChange={(e) => { setEmail(e.target.value); onClearError(); }}
            onBlur={() => setEmailTouched(true)}
            placeholder="you@example.com"
            autoComplete="email"
            disabled={isLoading}
            {...(emailError ? { 'aria-invalid': 'true' as const, 'aria-describedby': 'customer-email-error' } : {})}
          />
          {emailError && (
            <span id="customer-email-error" className={styles.fieldError} role="alert">
              {emailError}
            </span>
          )}
        </div>

        {/* Password field */}
        <div className={styles.fieldGroup}>
          <label htmlFor="customer-password" className={styles.label}>
            Password
          </label>
          <input
            id="customer-password"
            type="password"
            className={`${styles.input} ${passwordError ? styles.inputError : ''}`}
            value={password}
            onChange={(e) => { setPassword(e.target.value); onClearError(); }}
            onBlur={() => setPasswordTouched(true)}
            placeholder="Enter your password"
            autoComplete="current-password"
            disabled={isLoading}
            {...(passwordError ? { 'aria-invalid': 'true' as const, 'aria-describedby': 'customer-password-error' } : {})}
          />
          {passwordError && (
            <span id="customer-password-error" className={styles.fieldError} role="alert">
              {passwordError}
            </span>
          )}
        </div>

        {/* Submit */}
        <button type="submit" className={styles.submitButton} disabled={!canSubmit}>
          {isLoading && <span className={styles.spinner} aria-hidden="true" />}
          {isLoading ? 'Signing in…' : 'Sign In'}
        </button>
      </form>

      {/* Links */}
      <div className={styles.links}>
        <button type="button" className={styles.link} onClick={onForgotPassword} disabled={isLoading}>
          Forgot password?
        </button>
        <button type="button" className={styles.link} onClick={onCreateAccount} disabled={isLoading}>
          Create an account
        </button>
      </div>
    </>
  );
};

export default CustomerLoginForm;
